import React from 'react'

import BusinessIcon from '@mui/icons-material/Business'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import CheckIcon from '@mui/icons-material/Check'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import Filter1Icon from '@mui/icons-material/Filter1'
import Filter2Icon from '@mui/icons-material/Filter2'
import MultilineChartIcon from '@mui/icons-material/MultilineChart'
import PersonIcon from '@mui/icons-material/Person'
import RateReviewIcon from '@mui/icons-material/RateReview'
import SettingsIcon from '@mui/icons-material/Settings'
import WebIcon from '@mui/icons-material/Web'
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { Link, useLocation } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { styles } from './coreLayoutStyles'

const config = [
  [
    {
      name: 'Customers',
      icon: BusinessCenterIcon,
      link: UrlProvider.getUrl('customers'),
    },
    {
      name: 'Users',
      icon: PersonIcon,
      link: UrlProvider.getUrl('users'),
    },
    {
      name: 'Reveal API',
      icon: Filter1Icon,
      link: UrlProvider.getUrl('reveal'),
    },
    {
      name: 'Enrich API',
      icon: Filter2Icon,
      link: UrlProvider.getUrl('enrich'),
    },
  ],
  [
    {
      name: 'Campaigns Monitoring',
      icon: MultilineChartIcon,
      link: UrlProvider.getUrl('campaigns'),
    },
    {
      name: 'Campaigns Review',
      icon: CheckIcon,
      link: UrlProvider.getUrl('campaigns.review'),
    },
  ],
  [
    {
      name: 'Companies',
      icon: BusinessIcon,
      link: UrlProvider.getUrl('companies'),
    },
    {
      name: 'Data change requests',
      icon: RateReviewIcon,
      link: UrlProvider.getUrl('dataChangeRequests'),
    },
    {
      name: 'Name to domain',
      icon: WebIcon,
      link: UrlProvider.getUrl('nameToDomain'),
    },
  ],
  [
    {
      name: 'Account',
      icon: SettingsIcon,
      link: UrlProvider.getUrl('account'),
    },
  ],
]

// Longest match wins: /campaigns/review prefix-matches /campaigns too, and only the deeper item should light up.
export const activeLink = (pathname: string) =>
  config
    .flat()
    .map(({ link }) => link)
    .filter(link => link && (pathname === link || pathname.startsWith(`${link}/`)))
    .sort((a, b) => b.length - a.length)[0]

type CoreLayoutDrawerProps = {
  open: boolean
  onDrawerClose: (...args: any[]) => any
}

const CoreLayoutDrawer = ({ open, onDrawerClose }: CoreLayoutDrawerProps) => {
  const { pathname } = useLocation()
  const active = activeLink(pathname)

  const renderItems = ({ disabled, icon: IconComponent, name, link }: any, index: number) => {
    const props: any = {
      disabled,
      component: link ? Link : 'div',
      to: link || undefined,
      selected: !!link && link === active,
      sx: (theme: Theme) => ({
        justifyContent: open ? 'initial' : 'center',
        px: 2.5,
        '&.Mui-selected': {
          color: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderRight: 3,
          borderRightColor: 'primary.main',
          '& .MuiListItemIcon-root': { color: 'primary.main' },
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
        },
      }),
    }

    return (
      // Collapsed, the label is invisible and only the icon remains, so the name moves to a tooltip.
      <Tooltip key={index} placement="right" title={open ? '' : name}>
        <ListItemButton {...props}>
          <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}>
            <IconComponent />
          </ListItemIcon>
          <ListItemText primary={name} slotProps={{ primary: { noWrap: true } }} sx={{ opacity: open ? 1 : 0 }} />
        </ListItemButton>
      </Tooltip>
    )
  }

  const renderSection = (section: any[], index: number) => (
    <List disablePadding key={index}>
      {section.map(renderItems)}
      <Divider />
    </List>
  )

  return (
    <Drawer
      slotProps={{
        paper: {
          sx: (theme: any) => ({
            ...styles(theme).drawerPaper,
            ...(!open ? styles(theme).drawerPaperClose : {}),
          }),
        },
      }}
      variant="permanent">
      <Box sx={(theme: any) => styles(theme).toolbar}>
        <IconButton aria-label="Close drawer" onClick={onDrawerClose} size="large">
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      {config.map(renderSection)}
    </Drawer>
  )
}

export default CoreLayoutDrawer
